import Axios from 'axios';
import * as Routes from 'modules/routes';
import I18n from 'modules/i18n';
import React, { useEffect, useRef, useState } from 'react';
import Text from '@/components/ui/Text';
import Builder, { type SaveControl } from './index';

type BuilderProps = React.ComponentProps<typeof Builder>;

type OmittedBuilderProp = 'locale' | 'onReadyToSwitchLocale' | 'onSaveResult' | 'requestedLocale' | 'saveControlRef';

interface BilingualBuilderProps extends Omit<BuilderProps, OmittedBuilderProp> {
  initialLocale?: string;
}

interface LanguageChangeDetail {
  locale: string;
  questionId: number;
}

type ErrorKey = 'drag_drop_language_load_error' | 'drag_drop_save_error';

const BilingualBuilder = ({
  backgroundImage: initialBackgroundImage,
  data: initialData,
  initialLocale = 'en',
  questionId,
  ...props
}: BilingualBuilderProps) => {
  const [backgroundImage, setBackgroundImage] = useState(initialBackgroundImage);
  const [data, setData] = useState(initialData);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [locale, setLocale] = useState(initialLocale);
  const [requestedLocale, setRequestedLocale] = useState(initialLocale);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResubmitting = useRef(false);
  const saveControlRef = useRef<SaveControl | null>(null);
  const isSwitching = requestedLocale !== locale;

  useEffect(() => {
    // AIDEV-NOTE: ExerciseBuilder never unmounts the roots its form re-renders replace, so a stale instance
    // stays subscribed; isConnected keeps it from refetching on every tab click.
    const handleLanguageChange = (event: CustomEvent<LanguageChangeDetail>) => {
      if (!containerRef.current?.isConnected) return;
      if (event.detail.questionId === questionId) setRequestedLocale(event.detail.locale);
    };

    window.addEventListener('question-language-change', handleLanguageChange as EventListener);
    return () => window.removeEventListener('question-language-change', handleLanguageChange as EventListener);
  }, [questionId]);

  // AIDEV-NOTE: Block edits mid-switch; they'd save to the outgoing language or be dropped on remount.
  useEffect(() => {
    containerRef.current?.toggleAttribute('inert', isSwitching);
  }, [isSwitching]);

  // AIDEV-NOTE: The question form's Save re-renders this builder from the server, so hold that request
  // until our autosave lands. rails-ujs keeps the clicked button on the form, so re-firing submit keeps it.
  useEffect(() => {
    const form = containerRef.current?.closest('form');
    if (!form) return undefined;

    const handleBeforeFormSave = (event: Event) => {
      if (isResubmitting.current) {
        isResubmitting.current = false;
        return;
      }
      if (!saveControlRef.current?.hasPendingSave()) return;

      event.preventDefault();
      saveControlRef.current.flushPendingSave().then(() => {
        isResubmitting.current = true;
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    };

    form.addEventListener('ajax:before', handleBeforeFormSave);
    return () => form.removeEventListener('ajax:before', handleBeforeFormSave);
  }, []);

  const loadLocale = (nextLocale: string) => {
    Axios.get(Routes.plato_api_question_path(questionId, { locale: nextLocale }))
      .then((response) => {
        setBackgroundImage(response.data.question.background_image);
        setData(response.data.question.data);
        setLocale(nextLocale);
        setErrorKey(current => (current === 'drag_drop_language_load_error' ? null : current));
      })
      .catch(() => {
        setRequestedLocale(locale);
        setErrorKey('drag_drop_language_load_error');
      });
  };

  const handleSaveResult = (succeeded: boolean) => {
    setErrorKey(succeeded ? null : 'drag_drop_save_error');
  };

  return (
    <div ref={containerRef} aria-busy={isSwitching}>
      <div aria-live="assertive">
        {errorKey && (
          <Text className="tw-text-red" size="sm">
            {I18n.t(errorKey)}
          </Text>
        )}
      </div>
      <Builder
        key={locale}
        {...props}
        backgroundImage={backgroundImage}
        data={data}
        locale={locale}
        questionId={questionId}
        requestedLocale={requestedLocale}
        saveControlRef={saveControlRef}
        onReadyToSwitchLocale={loadLocale}
        onSaveResult={handleSaveResult}
      />
    </div>
  );
};

export default BilingualBuilder;
